/* eslint-disable camelcase */
/* eslint-disable react/jsx-no-constructed-context-values */
/**
 * Stores tokens from Entra Native Authentication sign-in so the app can use them for API calls
 * and treat the user as authenticated without MSAL.
 *
 * Silent refresh: the access token is short-lived (Entra issues ~30–60 min). The stored refresh
 * token is used to renew it — proactively a couple of minutes before expiry, on tab-focus, and as a
 * fallback on a 401 — so an ACTIVE user is never logged out mid-task (which previously dropped their
 * unsaved work when the token expired and API calls silently no-op'd).
 * @see https://learn.microsoft.com/en-us/entra/identity-platform/tutorial-native-authentication-single-page-app-react-sign-in
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { protectedResources, nativeAuthClientId } from "authConfig";

const STORAGE_KEY = "native_auth_tokens";
const EXPIRY_BUFFER_MS = 60 * 1000; // treat the token as expired 60s early (was an unintended 60ms)
const REFRESH_LEAD_MS = 2 * 60 * 1000; // proactively refresh 2 min before expiry
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // auto-logout after 30 min with no user interaction
const INACTIVITY_WARNING_MS = 60 * 1000; // show a "still there?" countdown for the last 60s
const IDLE_CHECK_MS = 1000; // 1s tick — drives a smooth countdown once the warning is showing

const apiBase = protectedResources?.apiTodoList?.endpoint || process.env.REACT_APP_API_URL || "";
const clientId = nativeAuthClientId || "";
const TOKEN_URL = `${apiBase}/native-auth/signin/token`;

const NativeAuthContext = createContext(null);
NativeAuthContext.displayName = "NativeAuthContext";

// Idle limit in ms. Normally 30 min; a tester can shorten it to verify the flow quickly without a
// 30-min wait, via ?idleMins=1 in the URL or localStorage.setItem('idleLimitMins','1'). Falls back to
// the 30-min default on anything invalid.
function idleLimitMs() {
  try {
    const q = new URLSearchParams(window.location.search).get("idleMins");
    const ls = window.localStorage.getItem("idleLimitMins");
    const mins = parseFloat(q || ls);
    if (Number.isFinite(mins) && mins > 0) return mins * 60 * 1000;
  } catch {
    /* ignore */
  }
  return INACTIVITY_LIMIT_MS;
}

function readStored() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

// The session is genuinely over (refresh token expired/revoked). Hard-redirect to the sign-in screen
// instead of leaving the app on a now-broken protected page — which is what produced the "click after
// 30 min idle → blank screen" (a component rendered against a dead session and threw). A full reload
// discards the broken React tree and lands cleanly on /signin. The module-level guard stops the many
// concurrent API calls a dead dashboard fires from each triggering their own navigation.
let redirecting = false;
function redirectToSignIn() {
  try {
    if (
      typeof window !== "undefined" &&
      !redirecting &&
      !window.location.pathname.startsWith("/signin")
    ) {
      redirecting = true;
      window.location.assign("/signin");
    }
  } catch {
    /* ignore */
  }
}

function loadStored() {
  const data = readStored();
  if (!data?.access_token) return null;
  // Keep a session with a still-refreshable token, even if the access token itself is stale — the
  // provider will renew it. Only drop it when there's no refresh path AND it's past expiry.
  if (data.expires_at && Date.now() >= data.expires_at - EXPIRY_BUFFER_MS && !data.refresh_token) {
    return null;
  }
  return data;
}

// "Are you still there?" countdown shown for the final INACTIVITY_WARNING_MS before auto-logout.
function IdleWarningDialog({ open, seconds, onStay, onLogout }) {
  return (
    <Dialog open={open} onClose={onStay} maxWidth="xs" fullWidth>
      <DialogTitle>Are you still there?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You&apos;ve been inactive for a while. For your security you&apos;ll be signed out in{" "}
          <b>{seconds}s</b>. Any unsaved changes will be lost.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onLogout} color="inherit">
          Log out now
        </Button>
        <Button onClick={onStay} variant="contained" autoFocus>
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}
IdleWarningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  seconds: PropTypes.number.isRequired,
  onStay: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

export function NativeAuthProvider({ children }) {
  const [tokens, setTokensState] = useState(loadStored);
  const refreshingRef = useRef(null);
  // Last time the user actually interacted. Initialised at mount and only ever moved forward by real
  // input — NEVER reset by a token refresh, so a silent refresh can't keep an idle session alive.
  const lastActivityRef = useRef(Date.now());
  // Idle-warning countdown: seconds left until auto-logout, or null when not warning. While it's
  // showing, input events do NOT reset the clock — the user must explicitly choose "Stay signed in".
  const [idleWarnSeconds, setIdleWarnSeconds] = useState(null);
  const warningActiveRef = useRef(false);

  useEffect(() => {
    const stored = loadStored();
    if (stored && !tokens) setTokensState(stored);
  }, []);

  const persist = useCallback(({ access_token, refresh_token, id_token, expires_in }) => {
    const expires_at = expires_in ? Date.now() + expires_in * 1000 : null;
    const data = { access_token, refresh_token, id_token, expires_in, expires_at };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setTokensState(data);
    return data;
  }, []);

  // setTokens is the SIGN-IN path (finishSignIn / signup). A fresh sign-in is genuine user activity,
  // so reset the inactivity clock to now — otherwise a sign-in page that sat open (or a short idle
  // limit) makes the brand-new session look already-idle, and the idle timer logs the user straight
  // back out (dashboard flashes, then bounces to /signin). Silent refresh keeps using persist()
  // directly, so it still does NOT count as activity.
  const setTokens = useCallback(
    (data) => {
      lastActivityRef.current = Date.now();
      return persist(data);
    },
    [persist]
  );

  const clearTokens = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setTokensState(null);
  }, []);

  // End the session cleanly: drop tokens and hard-redirect to /signin. Called whenever the app can no
  // longer obtain a usable token (idle/session timeout), so a click lands on the sign-in page instead
  // of a blank/unresponsive dashboard rendered against a dead session.
  const endSession = useCallback(() => {
    clearTokens();
    redirectToSignIn();
  }, [clearTokens]);

  // Whether a silent refresh could still recover the session (a refresh token is stored). API callers
  // use this to avoid logging an ACTIVE user out on a TRANSIENT refresh failure (network blip / proxy
  // 5xx) — only a genuinely dead session (no refresh token, e.g. after a definitive invalid_grant that
  // already cleared it) should bounce to sign-in.
  const sessionRecoverable = useCallback(() => Boolean(readStored()?.refresh_token), []);

  // Renew the access token from the stored refresh token. Concurrent callers share one in-flight
  // request. Returns the new access token, or null if refresh is impossible/failed.
  const refreshAccessToken = useCallback(async () => {
    if (refreshingRef.current) return refreshingRef.current;
    const rt = readStored()?.refresh_token;
    if (!rt || !clientId || !apiBase) return null;
    const run = (async () => {
      try {
        const res = await fetch(TOKEN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            client_id: clientId,
            grant_type: "refresh_token",
            refresh_token: rt,
            scope: "openid offline_access",
          }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok || !d.access_token) {
          // Definitive rejection of the refresh token (expired/revoked) → the session is genuinely
          // over; clear so the app redirects to sign-in. Transient network/5xx errors (the catch
          // below, or a 5xx here) leave the session intact so a later attempt can still recover.
          const err = String(d.error || "").toLowerCase();
          if (
            res.status === 401 ||
            (res.status === 400 &&
              (err.includes("invalid_grant") ||
                err.includes("invalid_token") ||
                err.includes("expired")))
          ) {
            clearTokens();
            redirectToSignIn(); // session over — go to sign-in cleanly, never a blank page
          }
          return null;
        }
        const next = persist({
          access_token: d.access_token,
          refresh_token: d.refresh_token || rt, // Entra rotates refresh tokens; keep old if absent
          id_token: d.id_token || d.access_token,
          expires_in: d.expires_in || 3600,
        });
        return next.access_token;
      } catch {
        return null;
      } finally {
        refreshingRef.current = null;
      }
    })();
    refreshingRef.current = run;
    return run;
  }, [persist, clearTokens]);

  const getAccessToken = useCallback(() => {
    const t = tokens || loadStored();
    if (!t?.access_token) return null;
    if (t.expires_at && Date.now() >= t.expires_at - EXPIRY_BUFFER_MS) return null;
    return t.access_token;
  }, [tokens]);

  // Async: return a usable token, refreshing first if the current one is expired/near-expiry.
  const getValidAccessToken = useCallback(async () => {
    const current = getAccessToken();
    if (current) return current;
    return refreshAccessToken();
  }, [getAccessToken, refreshAccessToken]);

  // Proactive refresh shortly before expiry, so an active user's token is renewed before it dies.
  useEffect(() => {
    if (!tokens?.expires_at || !tokens?.refresh_token) return undefined;
    const delay = Math.max(0, tokens.expires_at - Date.now() - REFRESH_LEAD_MS);
    const id = setTimeout(() => {
      refreshAccessToken();
    }, delay);
    return () => clearTimeout(id);
  }, [tokens?.expires_at, tokens?.refresh_token, refreshAccessToken]);

  // Background tabs throttle timers — top up on focus/visibility if we're inside the lead window.
  useEffect(() => {
    const onFocus = () => {
      const t = readStored();
      if (t?.refresh_token && t?.expires_at && Date.now() >= t.expires_at - REFRESH_LEAD_MS) {
        refreshAccessToken();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refreshAccessToken]);

  // Inactivity auto-logout: after INACTIVITY_LIMIT_MS with no user interaction, end the session so a
  // long-idle tab lands on /signin instead of a stale, half-dead dashboard. The clock is driven purely
  // by real input events (below) — the silent token refresh does NOT count as activity, so an idle
  // session is logged out on schedule even though its token could still be renewed. Only armed while a
  // session exists; on /signin there is nothing to end.
  const hasSession = Boolean(
    (tokens || loadStored())?.refresh_token || (tokens || loadStored())?.access_token
  );
  useEffect(() => {
    if (!hasSession) return undefined;
    const limit = idleLimitMs();
    const warnMs = Math.min(INACTIVITY_WARNING_MS, Math.max(5000, Math.floor(limit / 3)));
    // While the warning dialog is up, ignore input so the countdown isn't silently cancelled by a
    // stray mousemove — the user must click "Stay signed in".
    const bump = () => {
      if (warningActiveRef.current) return;
      // If the session already went past the idle limit while timers were frozen (machine asleep /
      // tab backgrounded for hours), the FIRST interaction on return must log out — NOT reset the
      // clock. Otherwise the click that wakes the tab would revive a dead session → blank screen.
      if (Date.now() - lastActivityRef.current >= limit) {
        endSession();
        return;
      }
      lastActivityRef.current = Date.now();
    };
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click", "mousemove"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const checkIdle = () => {
      const idle = Date.now() - lastActivityRef.current;
      if (idle >= limit) {
        warningActiveRef.current = false;
        setIdleWarnSeconds(null);
        endSession();
        return;
      }
      const remaining = limit - idle;
      if (remaining <= warnMs) {
        warningActiveRef.current = true;
        const secs = Math.max(1, Math.ceil(remaining / 1000));
        setIdleWarnSeconds((prev) => (prev === secs ? prev : secs));
      } else {
        warningActiveRef.current = false;
        setIdleWarnSeconds((prev) => (prev === null ? prev : null));
      }
    };
    const id = setInterval(checkIdle, IDLE_CHECK_MS);
    // Returning to a long-idle tab: check BEFORE the first mousemove can reset the clock. Background
    // tabs throttle the interval, so this is what actually catches "came back after 30+ min away".
    const onVisible = () => {
      if (document.visibilityState === "visible") checkIdle();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      clearInterval(id);
    };
  }, [hasSession, endSession]);

  // User chose to keep the session: reset the idle clock, dismiss the warning, and renew the access
  // token so the resumed session isn't running on a near-expired one.
  const stayActive = useCallback(() => {
    warningActiveRef.current = false;
    lastActivityRef.current = Date.now();
    setIdleWarnSeconds(null);
    refreshAccessToken();
  }, [refreshAccessToken]);

  const hasValidToken =
    Boolean(getAccessToken()) || Boolean((tokens || loadStored())?.refresh_token);

  const value = {
    tokens,
    setTokens,
    clearTokens,
    endSession,
    sessionRecoverable,
    getAccessToken,
    getValidAccessToken,
    refreshAccessToken,
    hasValidToken,
  };

  return (
    <NativeAuthContext.Provider value={value}>
      {children}
      <IdleWarningDialog
        open={idleWarnSeconds != null}
        seconds={idleWarnSeconds || 0}
        onStay={stayActive}
        onLogout={endSession}
      />
    </NativeAuthContext.Provider>
  );
}

NativeAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useNativeAuth() {
  const ctx = useContext(NativeAuthContext);
  return (
    ctx || {
      tokens: null,
      setTokens: () => {},
      clearTokens: () => {},
      endSession: () => {},
      sessionRecoverable: () => false,
      getAccessToken: () => null,
      getValidAccessToken: async () => null,
      refreshAccessToken: async () => null,
      hasValidToken: false,
    }
  );
}
